/**
 * Parse the OPF package document.
 */

export interface ManifestItem {
  href: string;
  type: string;
  overlay: string;
  properties: string[];
}

export interface SpineEntry {
  id: string | null;
  idref: string;
  linear: string;
  properties: string[];
  index: number;
}

export interface Metadata {
  title: string;
  creator: string;
  description: string;
  pubdate: string;
  publisher: string;
  identifier: string;
  language: string;
  rights: string;
  modified_date: string;
  layout: string;
  orientation: string;
  flow: string;
  viewport: string;
  media_active_class: string;
  spread: string;
  direction: string;
}

export interface PackagingResult {
  metadata: Metadata;
  manifest: Record<string, ManifestItem>;
  spine: SpineEntry[];
  navPath: string;
  ncxPath: string;
  coverPath: string;
  spineNodeIndex: number;
  uniqueIdentifier: string;
}

// Helper: query selector that works with namespaced XML
function qs(node: ParentNode, selector: string): Element | null {
  return node.querySelector(selector);
}

function qsa(node: ParentNode, selector: string): Element[] {
  return Array.from(node.querySelectorAll(selector));
}

function indexOfElementNode(node: Node): number {
  let index = 0;
  let child = node.parentNode?.firstChild;
  while (child) {
    if (child.nodeType === Node.ELEMENT_NODE) {
      if (child === node) return index;
      index++;
    }
    child = child.nextSibling;
  }
  return 0;
}

function getTextContent(parent: Element, tag: string): string {
  const el = qs(parent, tag);
  return el?.textContent?.trim() ?? "";
}

function parseManifest(manifestNode: Element): Record<string, ManifestItem> {
  const items = qsa(manifestNode, "item");
  const manifest: Record<string, ManifestItem> = {};
  for (const item of items) {
    const id = item.getAttribute("id") ?? "";
    manifest[id] = {
      href: item.getAttribute("href") ?? "",
      type: item.getAttribute("media-type") ?? "",
      overlay: item.getAttribute("media-overlay") ?? "",
      properties: (item.getAttribute("properties") ?? "")
        .split(/\s+/)
        .filter(Boolean),
    };
  }
  return manifest;
}

function parseSpine(
  spineNode: Element,
  manifest: Record<string, ManifestItem>,
): SpineEntry[] {
  const items = qsa(spineNode, "itemref");
  return items.map((item, index) => {
    const idref = item.getAttribute("idref") ?? "";
    const manifestItem = manifest[idref];
    return {
      id: manifestItem?.href ? idref : null,
      idref,
      linear: item.getAttribute("linear") ?? "yes",
      properties: (item.getAttribute("properties") ?? "")
        .split(/\s+/)
        .filter(Boolean),
      index,
    };
  });
}

function findNavPath(manifestNode: Element): string {
  const items = qsa(manifestNode, "item");
  for (const item of items) {
    const props = item.getAttribute("properties") ?? "";
    if (props.split(/\s+/).includes("nav")) {
      return item.getAttribute("href") ?? "";
    }
  }
  return "";
}

function findNcxPath(manifestNode: Element, spineNode: Element): string {
  // First try: look for toc attribute on spine
  const tocId = spineNode.getAttribute("toc");
  if (tocId) {
    const item = qs(manifestNode, `item[id="${tocId}"]`);
    if (item) return item.getAttribute("href") ?? "";
  }
  // Fallback: find by media-type
  const items = qsa(manifestNode, "item");
  for (const item of items) {
    if (item.getAttribute("media-type") === "application/x-dtbncx+xml") {
      return item.getAttribute("href") ?? "";
    }
  }
  return "";
}

function findCoverPath(
  doc: Document,
  manifest: Record<string, ManifestItem>,
): string {
  // EPUB3: look for properties="cover-image" in manifest
  for (const item of Object.values(manifest)) {
    if (item.properties.includes("cover-image")) {
      return item.href;
    }
  }
  // EPUB2: look for <meta name="cover" content="id">
  const meta = qs(doc, 'meta[name="cover"]');
  if (meta) {
    const coverId = meta.getAttribute("content") ?? "";
    if (manifest[coverId]) {
      return manifest[coverId].href;
    }
  }
  return "";
}

function findUniqueIdentifier(doc: Document): string {
  const pkg = qs(doc, "package");
  const uidRef = pkg?.getAttribute("unique-identifier") ?? "";
  if (uidRef) {
    const idEl = doc.getElementById(uidRef) ?? qs(doc, `[id="${uidRef}"]`);
    if (idEl) return idEl.textContent?.trim() ?? "";
  }
  return "";
}

function parseMetadata(metadataNode: Element): Metadata {
  return {
    title:
      getTextContent(metadataNode, "title") ||
      getTextContent(metadataNode, "dc\\:title"),
    creator:
      getTextContent(metadataNode, "creator") ||
      getTextContent(metadataNode, "dc\\:creator"),
    description:
      getTextContent(metadataNode, "description") ||
      getTextContent(metadataNode, "dc\\:description"),
    pubdate:
      getTextContent(metadataNode, "date") ||
      getTextContent(metadataNode, "dc\\:date"),
    publisher:
      getTextContent(metadataNode, "publisher") ||
      getTextContent(metadataNode, "dc\\:publisher"),
    identifier:
      getTextContent(metadataNode, "identifier") ||
      getTextContent(metadataNode, "dc\\:identifier"),
    language:
      getTextContent(metadataNode, "language") ||
      getTextContent(metadataNode, "dc\\:language"),
    rights:
      getTextContent(metadataNode, "rights") ||
      getTextContent(metadataNode, "dc\\:rights"),
    modified_date: "",
    layout: "",
    orientation: "",
    flow: "",
    viewport: "",
    media_active_class: "",
    spread: "",
    direction: "",
  };
}

export function parsePackaging(doc: Document): PackagingResult {
  const metadataNode = qs(doc, "metadata");
  if (!metadataNode) throw new Error("No metadata element in OPF");
  const manifestNode = qs(doc, "manifest");
  if (!manifestNode) throw new Error("No manifest element in OPF");
  const spineNode = qs(doc, "spine");
  if (!spineNode) throw new Error("No spine element in OPF");

  const manifest = parseManifest(manifestNode);
  const spine = parseSpine(spineNode, manifest);
  const metadata = parseMetadata(metadataNode);
  metadata.direction =
    spineNode.getAttribute("page-progression-direction") ?? "";

  // Look for modified_date in <meta property="dcterms:modified">
  const modMeta = qs(metadataNode, 'meta[property="dcterms:modified"]');
  if (modMeta) metadata.modified_date = modMeta.textContent?.trim() ?? "";

  // Look for rendition properties
  const renditionMetas = qsa(metadataNode, "meta[property]");
  for (const m of renditionMetas) {
    const prop = m.getAttribute("property") ?? "";
    const val = m.textContent?.trim() ?? "";
    if (prop === "rendition:layout") metadata.layout = val;
    else if (prop === "rendition:orientation") metadata.orientation = val;
    else if (prop === "rendition:flow") metadata.flow = val;
    else if (prop === "rendition:viewport") metadata.viewport = val;
    else if (prop === "rendition:spread") metadata.spread = val;
    else if (prop === "media:active-class") metadata.media_active_class = val;
  }

  return {
    metadata,
    manifest,
    spine,
    navPath: findNavPath(manifestNode),
    ncxPath: findNcxPath(manifestNode, spineNode),
    coverPath: findCoverPath(doc, manifest),
    spineNodeIndex: indexOfElementNode(spineNode),
    uniqueIdentifier: findUniqueIdentifier(doc),
  };
}
